import React from 'react';
import styled from '@emotion/styled';
import { Popover, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import type { PrjSchedule } from 'models/setup/PrjSchedule';
import { getScheduleStatus, getScheduleStyle } from 'utils/CalendarUtils';
import { IncompleteAlertBadge } from 'components/calendar/Calendar.styles';

interface EventMoreLayerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  dateStr: string;
  schedules: PrjSchedule[];
  selectedCategories?: Set<string>;
  onClose: () => void;
  onSelectSchedule: (schedule: PrjSchedule) => void;
}

const LayerContainer = styled.div`
  width: 320px;
  max-width: 90vw;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const LayerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LayerTitle = styled.span`
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
`;

const CountBadge = styled.span`
  padding: 1px 7px;
  border-radius: 10px;
  background-color: #e0f2fe;
  color: #0284c7;
  font-size: 11px;
  font-weight: 700;
`;

const LayerBody = styled.div`
  max-height: 340px;
  overflow-y: auto;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  /* 스크롤바 스타일 */
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
`;

const LayerEventItem = styled.div<{
  bg: string;
  borderColor: string;
  textColor: string;
  lineThrough: boolean;
  opacity: number;
}>`
  padding: 8px 10px;
  border-radius: 6px;
  background-color: ${({ bg }) => bg};
  border: ${({ borderColor }) => `1px solid ${borderColor}`};
  color: ${({ textColor }) => textColor};
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  opacity: ${({ opacity }) => opacity};

  &:hover {
    filter: brightness(0.96);
    transform: translateY(-1px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  }
`;

const EventItemHeader = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 3px;
`;

const EventTitleText = styled.span<{ fontWeight: number; lineThrough: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: ${({ lineThrough }) => (lineThrough ? 'line-through' : 'none')};
  flex: 1;
  font-weight: ${({ fontWeight }) => fontWeight};
`;

const EventTimeText = styled.div`
  font-size: 11px;
  opacity: 0.75;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const EventMoreLayer: React.FC<EventMoreLayerProps> = ({
  open,
  anchorEl,
  dateStr,
  schedules,
  selectedCategories,
  onClose,
  onSelectSchedule,
}) => {
  const formattedDate = dateStr ? dayjs(dateStr).format('M월 D일 (ddd)') : '';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 'none',
          backgroundColor: 'transparent',
          overflow: 'visible',
          maxHeight: 'none !important',
        },
      }}
    >
      <LayerContainer>
        <LayerHeader>
          <HeaderTitleGroup>
            <LayerTitle>{formattedDate}</LayerTitle>
            <CountBadge>{schedules.length}건</CountBadge>
          </HeaderTitleGroup>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="닫기"
            sx={{
              p: 0.5,
              color: '#64748b',
              '&:hover': { color: '#0f172a', bgcolor: '#e2e8f0' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </LayerHeader>

        <LayerBody>
          {schedules.map((sch) => {
            const _status = getScheduleStatus(sch, dateStr);
            const status = _status === 'today' ? 'normal' : _status;
            const category = sch.wktTpCd || 'ETC';
            const isDimmed = selectedCategories ? !selectedCategories.has(category) : false;
            const style = getScheduleStyle(status, sch.wktTpCd, isDimmed);
            const displayTitle = sch.schdDisplayTitle || sch.schdNm || '일정';
            const showProcAlert = sch.schdProcYn === 'Y';

            const startTime = sch.planStDtm ? dayjs(sch.planStDtm).format('HH:mm') : '';
            const endTime = sch.planEndDtm ? dayjs(sch.planEndDtm).format('HH:mm') : '';
            const isAllDay =
              sch.planStDtm &&
              sch.planEndDtm &&
              !dayjs(sch.planStDtm).isSame(dayjs(sch.planEndDtm), 'day');

            return (
              <LayerEventItem
                key={sch.schdId}
                bg={style.bg}
                borderColor={style.border}
                textColor={style.text}
                lineThrough={style.lineThrough}
                opacity={style.opacity}
                onClick={() => {
                  onSelectSchedule(sch);
                  onClose();
                }}
              >
                <EventItemHeader>
                  {showProcAlert && <IncompleteAlertBadge title="진행 중">!</IncompleteAlertBadge>}
                  <EventTitleText
                    fontWeight={sch.delayYn === 'Y' ? 600 : 500}
                    lineThrough={style.lineThrough}
                  >
                    {displayTitle}
                  </EventTitleText>
                </EventItemHeader>
                <EventTimeText>
                  <span>{isAllDay ? '종일 일정' : `${startTime} ~ ${endTime}`}</span>
                  {status === 'delayed' && (
                    <span style={{ color: '#dc2626', fontWeight: 700 }}>지연</span>
                  )}
                  {status === 'complete' && (
                    <span style={{ color: '#6b7280', fontWeight: 600 }}>완료</span>
                  )}
                </EventTimeText>
              </LayerEventItem>
            );
          })}
        </LayerBody>
      </LayerContainer>
    </Popover>
  );
};

export default EventMoreLayer;
